import React, { useState, useEffect, useCallback } from 'react';
import { Search, X, Calendar, Building, Briefcase, Brain, User, ChevronLeft, ChevronRight, Inbox, AwardIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/common/Button';
import { Eye } from 'lucide-react';
import interviewService, { SeriesDataResponse } from '../services/interviewService';
import CompanyLogo from '../components/common/CompanyLogo';
import { ParticleBackground } from '../components/ParticleBackground';
import { withPageTransition, PageTransitionItem } from '../utils/withTransition';

const BrowseSeriesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [interviewSeries, setInterviewSeries] = useState<SeriesDataResponse[]>([]);
  const [filteredSeries, setFilteredSeries] = useState<SeriesDataResponse[]>([]);
  const [isLoadingSeries, setIsLoadingSeries] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8; // Reduced items per page for 2-column layout

  // Smart pagination function to generate page numbers with ellipsis
  const generatePageNumbers = (currentPage: number, totalPages: number) => {
    const pages = [];
    const maxVisiblePages = 5; // Show 5 pages on desktop
    
    if (totalPages <= maxVisiblePages + 2) {
      // If total pages is small, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage <= 3) {
        // Show first few pages
        for (let i = 2; i <= Math.min(4, totalPages - 1); i++) {
          pages.push(i);
        }
        if (totalPages > 4) {
          pages.push('...');
        }
      } else if (currentPage >= totalPages - 2) {
        // Show last few pages
        if (totalPages > 4) {
          pages.push('...');
        }
        for (let i = Math.max(2, totalPages - 3); i < totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show pages around current page
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
      }
      
      // Always show last page
      pages.push(totalPages);
    }
    
    return pages;
  };

  // Fetch interview series
  const fetchInterviewSeries = useCallback(async () => {
    try {
      setIsLoadingSeries(true);
      const series = await interviewService.getInterviewSeriesList();
      setInterviewSeries(series);
      setFilteredSeries(series);
    } catch (error) {
      console.error('Error fetching interview series:', error);
      setInterviewSeries([]);
      setFilteredSeries([]);
    } finally {
      setIsLoadingSeries(false);
    }
  }, []);

  // Handle search query changes
  const handleSearchQueryChange = useCallback(async (query: string) => {
    setSearchQuery(query);
    setIsSearching(true);
    
    try {
      if (query.trim() === '') {
        // If search is empty, fetch all series
        const allSeries = await interviewService.getInterviewSeriesList();
        setFilteredSeries(allSeries);
      } else {
        // Use backend search API
        console.log('Searching for series with query:', query);
        const searchResults = await interviewService.getSeriesBySearch(query);
        console.log('Search results:', searchResults);
        setFilteredSeries(searchResults);
      }
    } catch (error) {
      console.error('Error during series search:', error);
      
      // Fallback to client-side filtering in case of API error
      const filtered = interviewSeries.filter(series => {
        const searchTerm = query.toLowerCase();
        return (
          series.name?.toLowerCase().includes(searchTerm) ||
          series.company?.toLowerCase().includes(searchTerm) ||
          series.role?.toLowerCase().includes(searchTerm) ||
          series.jobRoleTitle?.toLowerCase().includes(searchTerm) ||
          series.description?.toLowerCase().includes(searchTerm)
        );
      });
      setFilteredSeries(filtered);
    } finally {
      setIsSearching(false);
      setCurrentPage(1); // Reset to first page when searching
    }
  }, [interviewSeries]);

  const [isMobileView, setIsMobileView] = useState(false);

    // Check mobile view with improved detection based on Navbar.tsx approach
    useEffect(() => {
        const checkMobileView = () => {
          const width = window.innerWidth;
          const isMobile = width < 768;
          setIsMobileView(isMobile);
        };
    
        // Initialize on mount
        checkMobileView();
    
        // Set up ResizeObserver for more reliable detection
        const bodyResizeObserver = new ResizeObserver(() => {
          // We observe the body to detect viewport size changes
          checkMobileView();
        });
    
        bodyResizeObserver.observe(document.body);
    
        // Also listen for traditional events as fallback
        window.addEventListener('resize', checkMobileView);
    
        // Force a re-check after a short delay to handle some edge cases
        setTimeout(checkMobileView, 300);
    
        return () => {
          window.removeEventListener('resize', checkMobileView);
          bodyResizeObserver.disconnect();
        };
      }, []);

  // Load series on component mount
  useEffect(() => {
    fetchInterviewSeries();
  }, [fetchInterviewSeries]);

  // Handle search input changes with debouncing
  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      handleSearchQueryChange(searchQuery);
    }, 500);

    return () => clearTimeout(debounceTimeout);
  }, [searchQuery, handleSearchQueryChange]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredSeries.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSeries = filteredSeries.slice(startIndex, endIndex);

  // Custom background gradient styles
  const backgroundGradient = {
    background: 'radial-gradient(circle at 10% 20%, rgba(90, 92, 106, 0.05) 0%, rgba(32, 45, 58, 0.01) 81.3%)',
    position: 'relative' as 'relative',
    zIndex: 0,
    overflow: 'hidden' as 'hidden'
  };

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark" style={backgroundGradient}>
      {/* Animated gradient elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-[10%] left-[5%] w-[40%] h-[30%] rounded-full bg-primary-light/5 dark:bg-primary-dark/5 blur-[80px] animate-pulse-slow"></div>
        <div className="absolute bottom-[20%] right-[10%] w-[25%] h-[40%] rounded-full bg-secondary-light/5 dark:bg-secondary-dark/5 blur-[80px] animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-[40%] right-[30%] w-[20%] h-[20%] rounded-full bg-primary-light/3 dark:bg-primary-dark/3 blur-[80px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
      </div>
      
      <ParticleBackground />
      
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <PageTransitionItem index={1}>
            <h1 className="text-2xl md:text-3xl mt-6 font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-4">
              Browse All Series
            </h1>
            <p className="text-text-secondary-light dark:text-text-secondary-dark mb-8 max-w-3xl font-medium text-base">
              Explore comprehensive interview series from top companies. Each series includes multiple rounds designed to help you prepare for real-world interviews.
            </p>
          </PageTransitionItem>

          {/* Search field */}
          <PageTransitionItem index={2}>
            <div className="mb-8">
              <div className="max-w-2xl">
                <div className="flex items-center rounded-lg border border-border-light dark:border-border-dark px-3 py-1 bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all">
                  <Search size={18} className="text-text-tertiary-light dark:text-text-tertiary-dark mr-2" />
                  <input
                    type="text"
                    placeholder="Search by company, job role, or keyword..."
                    className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {isSearching ? (
                    <div className="animate-spin w-4 h-4 border-2 border-primary-light border-t-transparent rounded-full"></div>
                  ) : searchQuery.trim() !== '' ? (
                    <X size={18} className="text-text-secondary-light dark:text-text-secondary-dark cursor-pointer" onClick={() => setSearchQuery('')} />
                  ) : null}
                </div>
              </div>
            </div>
          </PageTransitionItem>

          {/* Results count */}
          {/* <div className={`mb-6 ${isPageLoaded ? 'dashboard-animate-in-delay-3' : 'opacity-0'}`}>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
              {isSearching ? 'Searching...' : `${filteredSeries.length} series found`}
            </p>
          </div> */}

          {/* Series grid */}
          {isLoadingSeries ? (
            <PageTransitionItem index={3}>
              <div className="flex flex-col items-center justify-center py-16">
                <div className="animate-spin w-12 h-12 border-4 border-primary-light border-t-transparent rounded-full mb-4"></div>
                <p className="text-text-secondary-light dark:text-text-secondary-dark">Loading interview series...</p>
              </div>
            </PageTransitionItem>
          ) : currentSeries.length > 0 ? (
            <>
              <PageTransitionItem index={3}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-8">
                  {currentSeries.map((series, index) => (
                    <div 
                      key={series._id} 
                      className="p-4 md:p-5 border rounded-lg border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark hover:border-primary-light/50 dark:hover:border-primary-dark/50 transition-all duration-300"
                    >
                      <div className="flex items-start">
                        {!isMobileView && (
                        <div className="mr-4 rounded-full flex-shrink-0">
                          <CompanyLogo 
                            fallbackIconSize={40} 
                            src={series.companyLogo || series.companyLogoUrl} 
                            alt="Logo" 
                            className="w-12 h-12 p-2 bg-background-light dark:bg-background-light object-contain rounded-full" 
                          />
                        </div>
                        )}
                        <div className="flex-1">
                            <div className={`flex ${isMobileView ? 'flex-col' : 'justify-between items-start'}`}>
                            {isMobileView && (
                            <div className='flex flex-row items-start justify-between'>
                                <div className="mb-2 rounded-full flex-shrink-0">
                                    {/* <TrendingUp size={38} className="p-2 text-primary-light dark:text-primary-dark rounded-full bg-background-light dark:bg-background-dark" /> */}
                                    <CompanyLogo fallbackIconSize={40} src={series.companyLogo || series.companyLogoUrl} alt="Logo" className="w-11 h-11 p-1.5 rounded-full object-contain rounded-full bg-background-light dark:bg-background-light" />
                                </div>
                                {Math.round((series.matchScore || series.match_score || 0) * 100) > 0 && (
                                <div className="flex items-center gap-2 -mr-1 mt-0 md:mt-0 min-w-[84px]">
                                    <span className={`inline-flex items-center text-xs bg-green-600/15 dark:bg-green-400/15 text-green-700 dark:text-green-400 px-2 py-1 rounded-full border border-green-600/20 dark:border-green-400/20`}>
                                    {Math.round((series.matchScore || series.match_score || 0) * 100)}% Match
                                    </span>
                                </div>
                                )}
                                </div>
                                  )}
                            <h3 className="font-heading font-semibold text-lg text-text-primary-light dark:text-text-primary-dark">
                              {series.name}
                            </h3>
                            {!isMobileView && Math.round((series.matchScore || series.match_score || 0) * 100) > 0 && (
                                <div className="-mr-1 inline-flex min-w-[84px] items-center text-xs bg-green-600/15 dark:bg-green-400/15 px-2 py-1 rounded-full border border-green-500/20 dark:border-green-400/20">
                                <span className="text-green-700 dark:text-green-400">
                                    {Math.round((series.matchScore || series.match_score || 0) * 100)}% Match
                                </span>
                                </div>
                            )}
                          </div>

                          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-2 mb-3">
                            {series.description}
                          </p>

                          <div className="flex flex-wrap items-center gap-2 mb-3">
                            {(series.totalRounds || series.interviews?.length) && (
                              <span className="inline-flex items-center text-xs bg-blue-600/15 dark:bg-blue-400/15 px-2 py-1 rounded-full border border-blue-600/20 dark:border-blue-400/20">
                                <Calendar size={12} className="mr-1 text-blue-600 dark:text-blue-400" />
                                <span className="text-blue-600 dark:text-blue-400">{series.totalRounds || series.interviews?.length} Round{series.totalRounds || series.interviews?.length > 1 ? 's' : ''}</span>
                              </span>
                            )}

                            {series.company && (
                              <span className="inline-flex items-center text-xs bg-secondary-light/10 dark:bg-secondary-dark/10 px-2 py-1 rounded-full border border-secondary-light/10 dark:border-secondary-dark/10">
                                <Building size={12} className="mr-1 text-green-600 dark:text-green-400" />
                                <span className="text-green-600 dark:text-green-400">{series.company}</span>
                              </span>
                            )}

                            {series.role && (
                              <span className="inline-flex items-center text-xs bg-purple-500/5 dark:bg-purple-400/5 px-2 py-1 rounded-full border border-purple-600/10 dark:border-purple-400/10">
                                <Briefcase size={12} className="mr-1 text-purple-600 dark:text-purple-400" />
                                <span className="text-purple-600 dark:text-purple-400">{series.role || series.jobRoleTitle || ''}</span>
                              </span>
                            )}

                            <span className={`inline-flex items-center text-xs ${series.difficulty <= 2 ? "bg-green-600/15 dark:bg-green-400/15" : series.difficulty <= 4 ? "bg-amber-600/15 dark:bg-amber-400/15" : "bg-red-600/15 dark:bg-red-400/15"} px-2 py-1 rounded-full border ${series.difficulty <= 2 ? "border-green-600/20 dark:border-green-400/20" : series.difficulty <= 4 ? "border-amber-600/20 dark:border-amber-400/20" : "border-red-600/20 dark:border-red-400/20"}`}>
                              <Brain size={12} className={`mr-1 ${series.difficulty <= 2 ? "text-green-600 dark:text-green-400" : series.difficulty <= 4 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400"}`} />
                              <span className={`${series.difficulty <= 2 ? "text-green-600 dark:text-green-400" : series.difficulty <= 4 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400"}`}>
                                {series.difficulty <= 2 ? "Easy" : series.difficulty <= 4 ? "Medium" : "Hard"}
                              </span>
                            </span>
                          </div>

                          <div className="flex flex-wrap items-center gap-4 mb-4 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                            <div className="flex items-center">
                              <User size={15} className="mr-1 text-primary-light dark:text-primary-dark" />
                              <span>{series.enrollmentCount > 0 ? (
                                  <span><span className='text-blue-600 dark:text-blue-400'>{series.enrollmentCount.toLocaleString()}</span>  <span className='text-text-primary-light dark:text-text-primary-dark'>Prepared</span></span>
                                ) : (
                                  <span className='text-text-primary-light dark:text-text-primary-dark'>Become the first to prepare</span>
                                )}</span>
                            </div>
                            <div className="flex items-center">
                              <AwardIcon size={14} className="mr-1 text-green-700 dark:text-green-400" />
                              <span className='text-green-700 dark:text-green-400'>{series.passScore}% <span className='text-text-primary-light dark:text-text-primary-dark'>Pass score</span></span>
                            </div>
                          </div>

                          <Button
                            variant="primary"
                            size="sm"
                            className='py-3 px-5'
                            onClick={() => navigate(`/interview-series/${series._id}`)}
                            leftIcon={<Eye size={14} />}
                          >
                            View Series
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </PageTransitionItem>

              {/* Pagination */}
              {totalPages > 1 && (
                <PageTransitionItem index={4}>
                  <div className="flex items-center justify-between pt-6 border-t border-border-light dark:border-border-dark">
                    <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                      Page {currentPage} of {totalPages} • {filteredSeries.length} series
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        leftIcon={<ChevronLeft size={14} />}
                        disabled={currentPage === 1}
                        onClick={() => {
                          setCurrentPage(prev => Math.max(1, prev - 1));
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        Previous
                      </Button>
                      
                      {/* Page numbers */}
                      <div className="flex items-center gap-1">
                        {generatePageNumbers(currentPage, totalPages).map((page, index) => {
                          if (typeof page === 'string') {
                            return (
                              <span
                                key={page}
                                className="px-3 py-2 text-text-secondary-light dark:text-text-secondary-dark text-sm"
                              >
                                ...
                              </span>
                            );
                          }
                          
                          return (
                            <Button
                              key={page}
                              variant={currentPage === page ? "primary" : "outline"}
                              size="sm"
                              className={`px-3 ${currentPage === page ? '' : 'text-text-secondary-light dark:text-text-secondary-dark'}`}
                              onClick={() => {
                                setCurrentPage(page);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }}
                            >
                              {page}
                            </Button>
                          );
                        })}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        rightIcon={<ChevronRight size={14} />}
                        disabled={currentPage === totalPages}
                        onClick={() => {
                          setCurrentPage(prev => Math.min(totalPages, prev + 1));
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </PageTransitionItem>
              )}
            </>
          ) : (
            <PageTransitionItem index={3}>
              <div className="text-center py-16">
                <div className="flex flex-col items-center gap-4">
                  <Inbox className="h-16 w-16 text-text-tertiary-light dark:text-text-tertiary-dark" />
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
                      No {searchQuery.trim() !== '' ? 'searched' : "recommended" } interview series found
                    </h3>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-6">
                      {searchQuery.trim() !== '' 
                        ? "It's not on you, it's on us. We're adding more & more series everyday to improve your preparation journey."
                        : 'Upload your resume to get personalized interview series recommendations or check back later for new series.'
                      }
                    </p>
                    <div className='flex items-center justify-center'>
                    {searchQuery.trim() !== '' ? (
                      <Button
                        onClick={() => setSearchQuery('')}
                        className="px-6 py-2"
                        variant="outline"
                      >
                        Clear Search
                      </Button>
                    ) : (
                      <Button
                        onClick={() => navigate('/settings-preferences?tab=preferences')}
                        className="px-6 py-2"
                        variant="outline"
                      >
                        Upload Resume
                      </Button>
                    )}
                    </div>
                  </div>
                </div>
              </div>
            </PageTransitionItem>
          )}
        </div>
      </main>
    </div>
  );
};

BrowseSeriesPage.displayName = 'BrowseSeriesPage';

// Export with page transition HOC
export default withPageTransition(BrowseSeriesPage); 